"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

type AssistantRouteHistoryContextValue = {
  canGoBack: boolean;
  goBack: () => void;
};

const AssistantRouteHistoryContext = createContext<
  AssistantRouteHistoryContextValue | undefined
>(undefined);

// Routes that should not count as a back-target. Mirrors the exclusions from
// the previous URL-stack implementation. RouteWatcher's replaceState mirrors
// don't update React Router's location, so they never reach this effect —
// only real Router pushes increment the depth.
function isBackTarget(pathname: string): boolean {
  if (!pathname.startsWith("/")) return false;
  if (pathname === "/" || pathname === "/settings") return false;
  if (pathname.startsWith("/login")) return false;
  return true;
}

export function AssistantRouteHistoryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const firstLocationRef = useRef(true);
  const [pushDepth, setPushDepth] = useState(0);

  useEffect(() => {
    if (firstLocationRef.current) {
      firstLocationRef.current = false;
      return;
    }
    if (!isBackTarget(location.pathname)) return;
    setPushDepth((d) => d + 1);
  }, [location.pathname, location.search]);

  const goBack = useCallback(() => {
    setPushDepth((d) => {
      if (d <= 0) return d;
      navigate(-1);
      return d - 1;
    });
  }, [navigate]);

  const value = useMemo<AssistantRouteHistoryContextValue>(
    () => ({
      canGoBack: pushDepth > 0,
      goBack,
    }),
    [goBack, pushDepth],
  );

  return (
    <AssistantRouteHistoryContext.Provider value={value}>
      {children}
    </AssistantRouteHistoryContext.Provider>
  );
}

export function useAssistantRouteHistory(): AssistantRouteHistoryContextValue {
  const context = useContext(AssistantRouteHistoryContext);
  if (!context) {
    throw new Error(
      "useAssistantRouteHistory must be used inside AssistantRouteHistoryProvider",
    );
  }
  return context;
}
